import React from 'react';
import { Link } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Logo from '@/components/Icons/Logo';
import { useLanding } from '@/components/Landing/LandingKit';
import { ACCENT, ACCENT_BRIGHT, ACCENT_TINT, SERIF } from '@/components/Landing/landingTokens';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';
import '@/app/styles/brand.css';

const ASSET_ROOT = '/brand-assets';
const LOGOS = [
  { id: 'green', name: 'Phthalo green', color: ACCENT, background: '#FAFAF7', use: 'For white and light backgrounds.' },
  { id: 'black', name: 'Black', color: '#040404', background: '#F1F3F0', use: 'For simple, single-color applications.' },
  { id: 'white', name: 'White', color: '#FFFFFF', background: ACCENT, use: 'For dark backgrounds.' },
];
const COLORS = [
  { name: 'Phthalo', hex: ACCENT, ink: '#FFFFFF', use: 'Primary brand color' },
  { name: 'Action green', hex: ACCENT_BRIGHT, ink: '#FFFFFF', use: 'Actions on dark surfaces' },
  { name: 'Mint', hex: ACCENT_TINT, ink: '#0F1512', use: 'Highlights on dark surfaces' },
  { name: 'Ink', hex: '#0F1512', ink: '#FFFFFF', use: 'Primary text' },
  { name: 'Paper', hex: '#FAFAF7', ink: '#0F1512', use: 'Light backgrounds' },
  { name: 'White', hex: '#FFFFFF', ink: '#0F1512', use: 'Cards and reversed logos' },
];

function SectionHeading({ number, title, children }) {
  return (
    <div className="gab-brand-section-heading">
      <span className="gab-brand-eyebrow">{number}</span>
      <div>
        <h2>{title}</h2>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

function DownloadLink({ id, format, name }) {
  const filename = `gab-ai-mark-${id}.${format}`;
  return (
    <a className="gab-brand-download" href={`${ASSET_ROOT}/${filename}`} download={filename} aria-label={`Download ${name.toLowerCase()} Gab AI logo as ${format.toUpperCase()}`}>
      {format.toUpperCase()} <span aria-hidden="true">↓</span>
    </a>
  );
}

export default function BrandPage() {
  const { c } = useLanding();
  return (
    <div
      className="gab-brand"
      style={{
        '--brand-page': c.page,
        '--brand-ink': c.ink,
        '--brand-copy': c.inkSoft,
        '--brand-rule': c.ruleStrong,
        '--brand-card': c.cardSolid,
        '--brand-accent': c.accent,
        '--brand-action': c.accentFill,
        '--brand-serif': SERIF,
        fontFamily: DEFAULT_FONT_FAMILY,
      }}
    >
      <MetaHead
        title="Brand Guidelines & Logos | Gab AI"
        description="Download Gab AI logos and find our brand colors, typography, and simple guidelines for using the Gab AI name and identity."
        canonical="/brand"
      />
      <main id="main-content" className="gab-brand-scroll" data-landing-scroll="true">
        <div className="gab-brand-content">
          <header className="gab-brand-hero">
            <div>
              <span className="gab-brand-eyebrow">Gab AI / Brand resources</span>
              <h1>Brand guidelines</h1>
              <p className="gab-brand-lede">Logos, colors, and a few simple rules for representing Gab AI.</p>
              <a className="gab-brand-primary" href={`${ASSET_ROOT}/gab-ai-logos.zip`} download="gab-ai-logos.zip">
                Download logo pack <span aria-hidden="true">↓</span>
              </a>
              <p className="gab-brand-meta">SVG and transparent PNG · Three color versions</p>
            </div>
            <div className="gab-brand-signature" role="img" aria-label="Gab AI logo in white on phthalo green">
              <div aria-hidden="true"><Logo width={132} height={132} color="#FFFFFF" /></div>
              <span aria-hidden="true">Gab AI</span>
            </div>
          </header>

          <section className="gab-brand-section" id="logos" aria-labelledby="brand-logos-title">
            <div className="gab-brand-section-heading">
              <span className="gab-brand-eyebrow">01</span>
              <div><h2 id="brand-logos-title">The Gab AI logo</h2><p>Use these files when writing about Gab AI, sharing our work, or linking to the product.</p></div>
            </div>
            <div className="gab-brand-logo-grid">
              {LOGOS.map((logo) => (
                <article className="gab-brand-logo-card" key={logo.id}>
                  <div className="gab-brand-logo-preview" style={{ background: logo.background }} role="img" aria-label={`${logo.name} Gab AI logo`}>
                    <div aria-hidden="true"><Logo width={112} height={112} color={logo.color} /></div>
                  </div>
                  <div className="gab-brand-logo-details">
                    <h3>{logo.name}</h3>
                    <p>{logo.use}</p>
                    <div className="gab-brand-download-row">
                      <DownloadLink id={logo.id} format="svg" name={logo.name} />
                      <DownloadLink id={logo.id} format="png" name={logo.name} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="gab-brand-note">SVG scales to any size. PNG files are 1024 × 1024 pixels with a transparent background.</p>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="02" title="Give the logo room">Keep the original proportions, orientation, and colors so the mark stays recognizable.</SectionHeading>
            <div className="gab-brand-usage-grid">
              <figure className="gab-brand-space-figure">
                <div className="gab-brand-clear-space" role="img" aria-label="Leave clear space equal to one quarter of the logo width on every side">
                  <div className="gab-brand-clear-mark" aria-hidden="true"><Logo width={144} height={144} color={ACCENT} /></div>
                </div>
                <figcaption>Leave at least ¼ of the mark’s width clear on every side.</figcaption>
              </figure>
              <div className="gab-brand-rules">
                <div><h3>Make it easy to see</h3><p>Choose the version with clear contrast against its background. Keep the mark at least 24 pixels wide on screen.</p></div>
                <div><h3>Keep the artwork intact</h3><p>Use the supplied files. Avoid stretching, rotating, cropping, recoloring, adding effects, or placing the logo over busy imagery.</p></div>
                <div><h3>Represent the relationship accurately</h3><p>Use the logo to identify Gab AI. Don’t combine it with another logo or suggest a partnership or endorsement that hasn’t been agreed.</p></div>
              </div>
            </div>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="03" title="Our colors">Deep green anchors the identity. Light neutrals give text and imagery room to breathe.</SectionHeading>
            <div className="gab-brand-palette">
              {COLORS.map((color) => (
                <div key={color.name} className="gab-brand-swatch" style={{ background: color.hex, color: color.ink }}>
                  <h3>{color.name}</h3><code>{color.hex}</code><p>{color.use}</p>
                </div>
              ))}
            </div>
            <p className="gab-brand-note">Use phthalo on light backgrounds and mint for emphasis on dark backgrounds. Keep body text contrast at 4.5:1 or higher.</p>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="04" title="Type and name">Keep the type familiar and the language direct.</SectionHeading>
            <div className="gab-brand-type-grid">
              <article className="gab-brand-type-card">
                <span className="gab-brand-type-sample" aria-hidden="true">Aa</span>
                <h3>A familiar sans serif</h3>
                <p>Our product uses native system fonts. For presentations, articles, and other documents, Arial is a practical fallback. Use a serif accent, such as Georgia italic, sparingly.</p>
              </article>
              <article className="gab-brand-type-card">
                <span className="gab-brand-name-sample">Gab AI</span>
                <h3>Two words. Capital G, A, and I.</h3>
                <p>Write “Gab AI” in text and use <a href="https://gab.ai">gab.ai</a> for our web address. Our short brand message is <em>AI that works.</em></p>
              </article>
            </div>
            <div className="gab-brand-description">
              <span className="gab-brand-eyebrow">Describing Gab AI</span>
              <p>Gab AI brings AI chat and useful work into one workspace. Explore ideas with different models, research a question, and create documents, presentations, spreadsheets, media, and code.</p>
            </div>
          </section>

          <aside className="gab-brand-contact">
            <div><h2>Need something else?</h2><p>Get in touch for a different format or a question about using the brand.</p></div>
            <Link href="/contact" asChild>
              <a className="gab-brand-contact-link">Contact us <span aria-hidden="true">↗</span></a>
            </Link>
          </aside>
        </div>
        <PublicFooter variant="editorial" showHero={false} />
      </main>
    </div>
  );
}
